import { isEqual } from 'lodash-es'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createContainer } from 'unstated-next'

import { violations } from '../combination-rules/engine'
import { PlacementId } from '../combination-rules/placementIds'
import { EmbroideryTypeKey } from '../combination-rules/types'

import { useEmbroideryGarment } from './EmbroideryGarmentProvider'
import { useSavedEmbroideryPreferences } from './EmbroideryPreferencesProvider'
import { makeEmbroideryItem } from './makeEmbroideryItem'
import {
  CommitResult,
  EmbroideryItem,
  EmbroideryItemDraft,
  EmbroideryItemEntry,
  EmbroideryItemValues,
} from './types'

const COMMITTED: CommitResult = { ok: true }

/** A selection to start from, such as one a page saved before unmounting the providers. */
export type EmbroiderySelectionInitialState = {
  items: readonly EmbroideryItem[]
  confirmed: boolean
}

type EmbroiderySelectionProviderProps = {
  /** The selection to start from instead of an empty one. Read once, on mount. */
  initialSelection?: EmbroiderySelectionInitialState
  /**
   * Called with the whole selection after every write, for a page to hand back as
   * `initialSelection` when it remounts the providers.
   */
  onSelectionChange?: (selection: EmbroiderySelectionInitialState) => void
}

/**
 * The embroidery the customer has chosen — one item per piece, each with its placement and its
 * values — and whether they have confirmed it, which is what lets it reach the bag.
 *
 * The only provider that writes, and every write goes through the engine first: a change that would
 * break the flow's rules is refused and names the rule that refused it, so the selection cannot become
 * illegal even when the UI offers something it should have disabled.
 */
function useEmbroiderySelectionImpl({
  initialSelection,
  onSelectionChange,
}: EmbroiderySelectionProviderProps = {}) {
  const { constraints } = useEmbroideryGarment()
  const { preferenceItems, isLoading: isLoadingPreferences } = useSavedEmbroideryPreferences()

  const [items, setItems] = useState<readonly EmbroideryItem[]>(initialSelection?.items ?? [])
  /** Whether the customer has confirmed the selection, which is what lets it reach the bag. */
  const [confirmed, setConfirmed] = useState(initialSelection?.confirmed ?? false)

  /**
   * The selection as of the last write, which is what a guard has to reason about.
   *
   * Two writes in one event handler would both see React's pre-batch state, so a guard reading
   * `items` would check the second against the list as it was before the first: it would accept a
   * write that breaks a rule, report `ok` for both, and then keep only the later one. This moves
   * with every write, so every guard sees what the one before it did. Render from `items`, decide
   * from this.
   */
  const itemsRef = useRef(items)
  /** The confirmation as of the last write, for the same reason as `itemsRef`. */
  const confirmedRef = useRef(confirmed)

  /**
   * The single write path, so nothing can update the state and leave the refs or the page behind.
   *
   * Every write replaces the items, but only `confirm`, `saveItemsOfType`, `clear` and `removeItem`
   * change the confirmation, so it is optional and defaults to the current one. It still goes through here
   * because it decides what reaches the bag: a page that restored the items without it would
   * leave the customer's embroidery out.
   */
  const writeSelection = useCallback(
    (nextItems: readonly EmbroideryItem[], nextConfirmed = confirmedRef.current) => {
      itemsRef.current = nextItems
      confirmedRef.current = nextConfirmed
      setItems(nextItems)
      setConfirmed(nextConfirmed)
      onSelectionChange?.({ items: nextItems, confirmed: nextConfirmed })
    },
    [onSelectionChange]
  )

  /**
   * Preferences seed the selection once, so a blank modal opens with them already chosen. It has to
   * be once: re-seeding would undo a customer who deleted a preferred item. A saved selection counts
   * as seeded, even an empty one, because the customer may have cleared it.
   */
  const hasSeeded = useRef(initialSelection !== undefined)
  useEffect(() => {
    if (hasSeeded.current || isLoadingPreferences || preferenceItems.length === 0) return
    hasSeeded.current = true
    if (itemsRef.current.length === 0) writeSelection(preferenceItems)
  }, [isLoadingPreferences, preferenceItems, writeSelection])

  /**
   * Writes the list only if the engine accepts it whole. Returns the first rule that objected, for
   * the caller to turn into a message.
   */
  const commit = useCallback(
    (next: readonly EmbroideryItem[]): CommitResult => {
      const [firstViolation] = violations(next, constraints)
      if (firstViolation) return { ok: false, reason: firstViolation.reason }
      writeSelection(next)
      return COMMITTED
    },
    [constraints, writeSelection]
  )

  /**
   * The reason the customer may not change this item, if there is one.
   */
  const refusalToEdit = useCallback((instanceId: string): CommitResult | undefined => {
    const target = itemsRef.current.find(item => item.instanceId === instanceId)
    if (!target) return { ok: false, reason: 'ITEM_NOT_FOUND' }
    if (target.source === 'ENFORCED') return { ok: false, reason: 'ITEM_NOT_EDITABLE' }
    return undefined
  }, [])

  const addItem = useCallback(
    (draft: EmbroideryItemDraft): CommitResult =>
      commit([...itemsRef.current, makeEmbroideryItem(draft, 'USER')]),
    [commit]
  )

  /**
   * Moves an item to another placement. Editing an item the customer never chose makes it theirs —
   * a preference they have changed is no longer a preference.
   */
  const moveItem = useCallback(
    (instanceId: string, placementId: PlacementId): CommitResult => {
      const refusal = refusalToEdit(instanceId)
      if (refusal) return refusal

      return commit(
        itemsRef.current.map(item =>
          item.instanceId === instanceId ? { ...item, placementId, source: 'USER' } : item
        )
      )
    },
    [commit, refusalToEdit]
  )

  /**
   * Replaces an item's values. Pass the item's own `typeKey`; it is checked against the item, so
   * values can never be written onto the wrong type of embroidery.
   */
  const setItemValues = useCallback(
    <K extends EmbroideryTypeKey>(
      instanceId: string,
      typeKey: K,
      values: EmbroideryItemValues[K]
    ): CommitResult => {
      const refusal = refusalToEdit(instanceId)
      if (refusal) return refusal
      const isOfType = itemsRef.current.some(
        item => item.instanceId === instanceId && item.typeKey === typeKey
      )
      if (!isOfType) return { ok: false, reason: 'ITEM_TYPE_MISMATCH' }

      return commit(
        itemsRef.current.map(item =>
          item.instanceId === instanceId
            ? ({ ...item, typeKey, values, source: 'USER' } as EmbroideryItem)
            : item
        )
      )
    },
    [commit, refusalToEdit]
  )

  /**
   * Deleting cannot break a rule, so this skips the engine — a missing required type is caught at
   * submit instead. It still refuses an enforced item: the flow put that one there and the customer
   * may not take it away, which is the whole of what `'ENFORCED'` means.
   *
   * Deleting the last thing the customer chose takes back the confirmation, as `clear` does, so
   * nothing goes on reporting embroidery that is no longer there.
   */
  const removeItem = useCallback(
    (instanceId: string): CommitResult => {
      const refusal = refusalToEdit(instanceId)
      if (refusal) return refusal

      const next = itemsRef.current.filter(item => item.instanceId !== instanceId)
      writeSelection(next, confirmedRef.current && next.some(item => item.source !== 'ENFORCED'))
      return COMMITTED
    },
    [refusalToEdit, writeSelection]
  )

  /**
   * Unchecks an item on the PDP without deleting it. Enforced items are refused, since dropping one
   * from the bag would undo the flow's choice just as surely as deleting it.
   */
  const setItemEnabledForBag = useCallback(
    (instanceId: string, enabledForBag: boolean): CommitResult => {
      const refusal = refusalToEdit(instanceId)
      if (refusal) return refusal

      writeSelection(
        itemsRef.current.map(item =>
          item.instanceId === instanceId ? { ...item, enabledForBag } : item
        )
      )
      return COMMITTED
    },
    [refusalToEdit, writeSelection]
  )

  /**
   * Takes a preference the customer ticked on the PDP without ever opening the modal. It stays
   * marked as a preference rather than a choice they made in the modal, which is what keeps it out
   * of anything that counts completed submissions.
   */
  const enableFromPdp = useCallback(
    (instanceId: string) => {
      writeSelection(
        itemsRef.current.map(item =>
          item.instanceId === instanceId && item.source === 'PREFERENCE'
            ? { ...item, enabledForBag: true, source: 'PDP_PREFERENCE' }
            : item
        )
      )
    },
    [writeSelection]
  )

  /**
   * Saves an editor's work on one type of embroidery and confirms the selection, in one write. An
   * empty `entries` takes the type off the garment.
   *
   * This means exiting the editor screen would return the unsaved entry back to what it previously was.
   **/
  const saveItemsOfType = useCallback(
    <K extends EmbroideryTypeKey>(
      typeKey: K,
      entries: readonly EmbroideryItemEntry<K>[]
    ): CommitResult => {
      const next: EmbroideryItem[] = []
      for (const item of itemsRef.current) {
        if (item.typeKey !== typeKey) {
          next.push(item)
          continue
        }

        const entry = entries.find(candidate => candidate.instanceId === item.instanceId)
        const changed =
          entry !== undefined &&
          (entry.placementId !== item.placementId || !isEqual(entry.values, item.values))
        if (item.source === 'ENFORCED' && (entry === undefined || changed)) {
          return { ok: false, reason: 'ITEM_NOT_EDITABLE' }
        }
        if (entry === undefined) continue

        next.push(
          changed
            ? ({
                ...item,
                placementId: entry.placementId,
                values: entry.values,
                source: 'USER',
              } as EmbroideryItem)
            : item
        )
      }

      const kept = new Set(next.map(item => item.instanceId))
      for (const entry of entries) {
        if (entry.instanceId !== undefined && kept.has(entry.instanceId)) continue
        const draft = { typeKey, placementId: entry.placementId, values: entry.values }
        next.push(makeEmbroideryItem(draft as EmbroideryItemDraft, 'USER'))
      }

      const [firstViolation] = violations(next, constraints)
      if (firstViolation) return { ok: false, reason: firstViolation.reason }
      // Saving away the last thing the customer chose takes back the confirmation, as `removeItem` does.
      writeSelection(
        next,
        next.some(item => item.source !== 'ENFORCED')
      )
      return COMMITTED
    },
    [constraints, writeSelection]
  )

  // Editing the selection afterwards leaves it confirmed. Only `clear` and deleting the last chosen
  // item take it back.
  const confirm = useCallback(() => writeSelection(itemsRef.current, true), [writeSelection])

  /**
   * Drops everything the customer chose and takes back the confirmation. What the flow enforces
   * survives, so clearing leaves the selection where it started rather than emptier than any
   * starting point the flow allows. Preferences do not seed it again afterwards, so the customer is
   * left with a blank selection rather than their saved one.
   */
  const clear = useCallback(
    () =>
      writeSelection(
        itemsRef.current.filter(item => item.source === 'ENFORCED'),
        false
      ),
    [writeSelection]
  )

  /** What the customer has left checked, whether or not they have confirmed it yet. */
  const itemsEnabledForBag = useMemo(() => items.filter(item => item.enabledForBag), [items])

  /**
   * What actually goes in the bag. Until the customer confirms, only what they ticked on the PDP
   * counts; anything typed into the modal and then abandoned stays out.
   */
  const itemsForBag = useMemo(
    () =>
      confirmed
        ? itemsEnabledForBag
        : itemsEnabledForBag.filter(item => item.source === 'PDP_PREFERENCE'),
    [confirmed, itemsEnabledForBag]
  )

  return {
    items,
    itemsEnabledForBag,
    itemsForBag,
    confirmed,
    addItem,
    moveItem,
    setItemValues,
    removeItem,
    setItemEnabledForBag,
    enableFromPdp,
    saveItemsOfType,
    confirm,
    clear,
  }
}

const EmbroiderySelectionContainer = createContainer(useEmbroiderySelectionImpl)

export const EmbroiderySelectionProvider = EmbroiderySelectionContainer.Provider
export const useEmbroiderySelection = EmbroiderySelectionContainer.useContainer
